import React, { useEffect } from 'react';
import styled, { useTheme } from 'styled-components/native';
import { UseInfiniteQueryResult } from '@tanstack/react-query';
import { TextInput, ViewProps } from 'react-native';
import { useDebounce } from '@/hooks/use.debounce';
import { Box } from '@/components/base/box';
import { AppIconButton } from '@/components/base/app.icon.button';
import { AppTextField } from '@/components/base/app.text.field';
import { AppSpinner } from '@/components/base/app.spinner';
import { hpVal, numbersToEnglish } from '../../utils';
import { AppCardView } from '@/components/base/app.card.view';
import { applyOpacity } from '@/assets/theme';

interface Props {
  api: UseInfiniteQueryResult;
  placeHolder: string;
  initialValue?: string;
  onChange: (text: string) => void;
  onFocus?: (focused: boolean) => void;
  fullWidth?: boolean;
  style?: ViewProps['style'];
  ref?: React.ForwardedRef<TextInput>;
}

export const AppSearchInput = React.memo(
  React.forwardRef(({ api, initialValue, placeHolder, onFocus, onChange, style, fullWidth = true }: Props, ref: React.ForwardedRef<TextInput>) => {
    const theme = useTheme();
    const { origin: search, debounced, setDebounce: setRaw } = useDebounce(String(initialValue || ''), 500);

    useEffect(() => {
      onChange(numbersToEnglish(debounced));
    }, [onChange, debounced]);

    return (
      <Container
        fullWidth={fullWidth}
        flexDirection={'row-reverse'}
        alignItems={'center'}
        style={style}
        shadowColor={theme.colors.softBlue}
        shadowOpacity={1}
        blurRadius={10}
        shadowOffsetX={0}
        shadowOffsetY={0}
      >
        <TextField
          floatingLabel={false}
          underline={false}
          focusable={false}
          ref={ref}
          border={false}
          onFocus={onFocus}
          placeholder={placeHolder}
          value={search}
          onChangeText={setRaw}
        />
        <Line />
        {api.isFetching && <Indicator />}
        {!api.isFetching && <SearchButton icon={'search'} size={'medium'} color={theme.colors.icon.grey_dark} />}
      </Container>
    );
  }),
);

const Container = styled(AppCardView)`
  max-height: ${({ theme }) => theme.hp(8)}px;
  border-radius: ${({ theme }) => theme.hp(1.2)}px;
  border-color: ${({ theme }) => applyOpacity(theme.colors.jadeDark, 0.1)};
  border-width: 0.5px;
`;

const TextField = styled(AppTextField)`
  width: 100%;
  height: 100%;
  flex: 1;
`;

const Line = styled(Box)`
  width: 1px;
  background-color: ${({ theme }) => theme.colors.textField.search_border};
  height: ${hpVal(5)};
`;

const SearchButton = styled(AppIconButton)`
  padding: 15px;
`;

const Indicator = styled(AppSpinner)`
  margin-left: 15px;
  margin-right: 15px;
  width: 30px;
  height: 30px;
`;
